/**
 * useNearestOffice — browser-geolocation-driven nearest-office resolver.
 *
 * Given an array of office/location docs (each with a `coordinates: {lat,lng}` field),
 * request browser geolocation once and return the closest office by haversine distance.
 *
 * Returns:
 *   status       'idle' | 'requesting' | 'ready' | 'denied' | 'unavailable' | 'error'
 *   nearest      The full office doc closest to the user (or null if not resolved yet)
 *   distanceKm   Great-circle distance to the nearest office in km (rounded to 1 decimal)
 *   ranked       All offices sorted ascending by distance: [{ office, distanceKm }, ...]
 *   userCoords   { lat, lng } if geolocation was granted, else null
 *   retry        Function to re-request permission (e.g., after user grants it manually)
 */
import { useEffect, useState, useCallback } from 'react';

const EARTH_RADIUS_KM = 6371;

const haversine = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const useNearestOffice = (offices) => {
  const [status, setStatus] = useState('idle');
  const [userCoords, setUserCoords] = useState(null);
  const [nearest, setNearest] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [ranked, setRanked] = useState([]);

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('unavailable');
      return;
    }
    setStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setStatus('ready');
      },
      (err) => {
        setStatus(err && err.code === 1 ? 'denied' : 'error');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // Ask for geolocation once when the hook mounts.
  useEffect(() => {
    request();
  }, [request]);

  // Whenever we have both coords and offices, compute the closest.
  useEffect(() => {
    if (!userCoords || !Array.isArray(offices) || offices.length === 0) return;
    const scored = [];
    for (const o of offices) {
      const c = o.coordinates;
      if (!c || typeof c.lat !== 'number' || typeof c.lng !== 'number') continue;
      const d = haversine(userCoords.lat, userCoords.lng, c.lat, c.lng);
      scored.push({ office: o, distanceKm: Math.round(d * 10) / 10 });
    }
    scored.sort((a, b) => a.distanceKm - b.distanceKm);
    setRanked(scored);
    if (scored.length > 0) {
      setNearest(scored[0].office);
      setDistanceKm(scored[0].distanceKm);
    }
  }, [userCoords, offices]);

  return { status, userCoords, nearest, distanceKm, ranked, retry: request };
};

export default useNearestOffice;
